/**
 * Auth state utilities and derived computations.
 * Framework-agnostic — pure functions operating on AuthState.
 */
// ─── Derived State ──────────────────────────────────────────────────
/**
 * Get auth status summary for UI display.
 */
export function getAuthStatus(state) {
    const user = state.user;
    const displayName = user?.username ?? 'Guest';
    const initials = getInitials(displayName);
    return {
        isLoggedIn: state.isAuthenticated,
        isLoading: state.isLoading,
        hasError: state.error !== null,
        displayName,
        initials,
    };
}
/**
 * Get token information (without exposing actual tokens).
 */
export function getTokenInfo(state) {
    return {
        hasAccessToken: state.accessToken !== null,
        hasRefreshToken: state.refreshToken !== null,
    };
}
/**
 * Extract user stats for display.
 */
export function getUserStats(user) {
    const winRate = user.gamesPlayed > 0
        ? Math.round((user.gamesWon / user.gamesPlayed) * 100)
        : 0;
    return {
        gamesPlayed: user.gamesPlayed,
        gamesWon: user.gamesWon,
        winRate,
        rating: user.rating,
    };
}
/**
 * Get rating tier label from ELO rating.
 */
export function getRatingTier(rating) {
    if (rating >= 2000)
        return { label: 'Master', color: 'purple', minRating: 2000 };
    if (rating >= 1800)
        return { label: 'Expert', color: 'red', minRating: 1800 };
    if (rating >= 1600)
        return { label: 'Advanced', color: 'orange', minRating: 1600 };
    if (rating >= 1400)
        return { label: 'Intermediate', color: 'blue', minRating: 1400 };
    if (rating >= 1200)
        return { label: 'Novice', color: 'green', minRating: 1200 };
    return { label: 'Beginner', color: 'gray', minRating: 0 };
}
// ─── Utilities ──────────────────────────────────────────────────────
/**
 * Extract initials from a name (up to 2 characters).
 */
export function getInitials(name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
}
/**
 * Format rating with sign (e.g., "+15", "-8", "0").
 */
export function formatRatingChange(change) {
    if (change > 0)
        return `+${change}`;
    return `${change}`;
}
//# sourceMappingURL=useAuth.js.map